import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import * as AuthActions from './auth.actions';
import { AuthService } from '../auth.service';
import { Observable, of } from 'rxjs';
import { exhaustMap, map, catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AuthData } from '../auth-data.model';
import { MessageService } from 'primeng/api';

/**
 * Effects offer a way to isolate and easily test side-effects within your
 * application. StateUpdates is an observable of the latest state and
 * dispatched action. The `toPayload` helper function returns just
 * the payload of the currently dispatched action, useful in
 * instances where the current state is not necessary.
 *
 * If you are unfamiliar with the operators being used in these examples, please
 * check out the sources below:
 *
 * Official Docs: http://reactivex.io/rxjs/manual/overview.html#categories-of-operators
 * RxJS 5 Operators By Example: https://gist.github.com/btroncone/d6cf141d6f2c00dc6b35
 */

@Injectable()
export class AuthEffects {

    constructor(
        private actions$: Actions,
        private authService: AuthService,
        private messageService: MessageService,
        private router: Router
    ) { }


    @Effect()
    doLogin: Observable<AuthActions.AuthActionsAll> =
        this.actions$
            .pipe(
                ofType<AuthActions.DoLoginAction>(AuthActions.DO_LOGIN),
                exhaustMap((action) => {
                    return this.authService.login({ username: action.payload.username, password: action.payload.password })
                        .pipe(
                            map((authResponse: AuthData) => {
                                authResponse.tokenExpirationDate = new Date(authResponse.tokenExpirationDate);
                                return new AuthActions.DoLoginSuccessAction(authResponse);
                            }),
                            catchError(err => {
                                console.error(err);
                                return of(new AuthActions.DoLoginFailAction(err));
                            })
                        );
                })
            );

    @Effect({ dispatch: false })
    doLoginSuccess =
        this.actions$
            .pipe(
                ofType<AuthActions.DoLoginSuccessAction>(AuthActions.DO_LOGIN_SUCCESS),
                map((action: any) => {
                    localStorage.setItem("authData", JSON.stringify(action.payload));
                    this.authService.autoLogout(new Date(action.payload.tokenExpirationDate));
                }));

    @Effect({ dispatch: false })
    loginFailure$ = this.actions$.pipe(
        ofType(AuthActions.DO_LOGIN_FAIL),
        map(action => this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Invalid username or password' }))
    );

    @Effect({ dispatch: false })
    doLogout =
        this.actions$
            .pipe(
                ofType<AuthActions.DoLogoutAction>(AuthActions.DO_LOGOUT),
                map((action: any) => {
                    localStorage.removeItem('authData');
                    this.authService.clearTokenExpirationTimer();
                    this.router.navigate(['/login']);
                }));


    @Effect({ dispatch: false })
    doAutoLoginFail =
        this.actions$
            .pipe(
                ofType<AuthActions.DoAutoLoginFailAction>(AuthActions.DO_AUTO_LOGIN_FAIL),
                map((action: any) => {
                    localStorage.removeItem('authData');
                    this.router.navigate(['/login']);
                }));
    /**
  * Registers effect
   
  @Effect()
  doRegister$: Observable<Action> = this.actions$
  .ofType(AuthActions.DO_REGISTER)
  .map((action: AuthActions.DoRegisterAction) => action.payload)
  .switchMap(state => {
    return this.authService.register(state)
      .map(user => new AuthActions.DoRegisterSuccessAction(new User(user)))
      .catch(error => of(new AuthActions.DoRegisterFailAction()));
  });
  
  /**
  * Logout effect
  
  @Effect()
  doLogout$: Observable<Action> = this.actions$
  .ofType(AuthActions.DO_LOGOUT)
  .map((action: AuthActions.DoLogoutAction) => null)
  .switchMap(state => {
    return this.authService.logout()
      .map(() => new AuthActions.DoLogoutSuccessAction())
      .catch(error => of(new AuthActions.DoLogoutFailAction()));
  });
  
     */
}